/**
=========================================================
* Soft UI Dashboard React - v3.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiBadge from "components/SuiBadge";
import Tooltip from "@mui/material/Tooltip";

// Timeline context
import { useTimeline } from "examples/Timeline/context";

// Custom styles for the TimelineItem
import { timelineItem, timelineItemIcon } from "examples/Timeline/TimelineItem/styles";

// Moment timezone
import moment from "moment";

function TimelineItem({
  color,
  icon,
  title,
  dateTime,
  recordTimestamp,
  comment,
  description,
  badges,
  lastItem,
  onDateTimeUpdate,
  isUnresolvedComment,
  onResolve,
}) {
  const isDark = useTimeline();
  const [editing, setEditing] = useState(false);
  const [newDateTime, setNewDateTime] = useState(
    moment.unix(recordTimestamp).format("YYYY-MM-DDTHH:mm")
  );

  // Sync newDateTime if recordTimestamp changes
  useEffect(() => {
    setNewDateTime(moment.unix(recordTimestamp).format("YYYY-MM-DDTHH:mm"));
  }, [recordTimestamp]);

  const handleSave = (value = newDateTime) => {
    setEditing(false);
    const originalDateTime = moment.unix(recordTimestamp).format("YYYY-MM-DDTHH:mm");
    if (value !== originalDateTime && onDateTimeUpdate) {
      onDateTimeUpdate(value);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setNewDateTime(moment.unix(recordTimestamp).format("YYYY-MM-DDTHH:mm"));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  const renderBadges =
    badges.length > 0
      ? badges.map((badge, key) => (
          <SuiBox key={`badge-${key}`} mr={key === badges.length - 1 ? 0 : 0.5}>
            <SuiBadge color={color} size="xs" badgeContent={badge} container />
          </SuiBox>
        ))
      : null;

  return (
    <SuiBox position="relative" sx={(theme) => timelineItem(theme, { lastItem })}>
      <SuiBox
        bgColor={isDark ? "dark" : "white"}
        width="1.625rem"
        height="1.625rem"
        borderRadius="50%"
        position="absolute"
        top="3.25%"
        left="2px"
        zIndex={2}
      >
        <Icon sx={(theme) => timelineItemIcon(theme, { color })}>{icon}</Icon>
      </SuiBox>
      <SuiBox ml={5.75} pt={description ? 0.7 : 0.5} lineHeight={0} maxWidth="30rem">
        <SuiTypography variant="button" fontWeight="medium" color={isDark ? "white" : "dark"}>
          {title}
          {(comment || onDateTimeUpdate) && (
            <SuiBox component="span" display="inline-flex" alignItems="center" ml={1}>
              {comment && (
                <Tooltip title={comment} placement="top">
                  <Icon
                    color={isUnresolvedComment ? "error" : "secondary"}
                    sx={{ cursor: "pointer", mr: 0.5 }}
                  >
                    {isUnresolvedComment ? "report_problem" : "info"}
                  </Icon>
                </Tooltip>
              )}

              {onDateTimeUpdate && (
                <Tooltip title="Edit time" placement="top">
                  <Icon
                    color="action"
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setNewDateTime(moment.unix(recordTimestamp).format("YYYY-MM-DDTHH:mm"));
                      setEditing(true);
                    }}
                  >
                    edit
                  </Icon>
                </Tooltip>
              )}

              {isUnresolvedComment && onResolve && (
                <Tooltip title="Mark resolved" placement="top">
                  <Icon
                    color="success"
                    sx={{ cursor: "pointer", ml: 0.5 }}
                    onClick={onResolve}
                  >
                    check_circle
                  </Icon>
                </Tooltip>
              )}
            </SuiBox>
          )}
        </SuiTypography>

        <SuiBox mt={0.5}>
          {/* Existing formatted dateTime */}
          <SuiTypography
            variant="caption"
            fontWeight="medium"
            color={isDark ? "secondary" : "text"}
          >
            {dateTime}
          </SuiTypography>

          {/* Editable datetime input */}
          {editing && (
            <SuiBox
              display="flex"
              alignItems="center"
              mt={0.75}
              px={1}
              py={0.5}
              borderRadius="lg"
              sx={(theme) => ({
                background: theme.palette.grey[100],
                border: `1px solid ${theme.palette.grey[300]}`,
                width: "fit-content",
              })}
            >
              <input
                type="datetime-local"
                value={newDateTime}
                onChange={(e) => setNewDateTime(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: "12px",
                  fontFamily: "inherit",
                  color: "#344767",
                }}
                autoFocus
              />

              {/* Save */}
              <Icon
                color="success"
                fontSize="small"
                sx={{ cursor: "pointer", ml: 1 }}
                onClick={() => handleSave()}
              >
                check
              </Icon>

              {/* Cancel */}
              <Icon
                color="error"
                fontSize="small"
                sx={{ cursor: "pointer", ml: 0.5 }}
                onClick={handleCancel}
              >
                close
              </Icon>
            </SuiBox>
          )}
        </SuiBox>

        {description && (
          <SuiBox mt={2} mb={1.5}>
            <SuiTypography variant="button" fontWeight="regular" color="text">
              {description}
            </SuiTypography>
          </SuiBox>
        )}

        {badges.length > 0 && (
          <SuiBox display="flex" pb={lastItem ? 1 : 2}>
            {renderBadges}
          </SuiBox>
        )}
      </SuiBox>
    </SuiBox>
  );
}

// Setting default values for the props of TimelineItem
TimelineItem.defaultProps = {
  color: "info",
  badges: [],
  lastItem: false,
  description: "",
  onDateTimeUpdate: null,
  isUnresolvedComment: false,
  onResolve: null,
};

// Typechecking props for the TimelineItem
TimelineItem.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  dateTime: PropTypes.string.isRequired,
  comment: PropTypes.string,
  description: PropTypes.string,
  badges: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  lastItem: PropTypes.bool,
  onDateTimeUpdate: PropTypes.func,
  isUnresolvedComment: PropTypes.bool,
  onResolve: PropTypes.func,
};

export default TimelineItem;
